import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import {usePost} from '../hooks/'
import { tenant,getCompanyById } from '../utils/tenantManager.js';
import { arrayToObjKeyValue } from '../utils/global.js';

// A real serial number: has a digit and is not a JavaScript failure ("NaN", "undefined", "null").
export const isValidInvNo = (value) => /\d/.test(String(value ?? '')) && !/nan|undefined|null/i.test(String(value ?? ''));

// Next serial number from the last one saved, following the company's INV_NO_FORMAT setting.
// It only ever increases the trailing digits of the last number (keeping its prefix and zero padding),
// so a missing INV_NO_START or an unexpected last number can no longer give "NaN" (that is how an
// invoice got the number NaN). Falls back to INV_NO_START + 1, or 1.
export const nextInvNo = (data, SETTING_INVOICE = {}, userPrefix = '') => {
    const parsedStart = parseInt(SETTING_INVOICE.INV_NO_START, 10);
    const start = Number.isFinite(parsedStart) ? parsedStart : 0;
    const last = isValidInvNo(data) ? String(data).trim() : '';
    const match = /^(.*?)(\d+)(\D*)$/.exec(last);
    const increment = () => {
        const next = String(parseInt(match[2], 10) + 1).padStart(match[2].length, '0');
        return `${match[1]}${next}${match[3]}`;
    };

    if (SETTING_INVOICE.INV_NO_FORMAT === 'Number') {
        return match ? parseInt(match[2], 10) + 1 : start + 1;
    }
    if (SETTING_INVOICE.INV_NO_FORMAT === 'UserAndNumber') {
        return match ? increment() : `${userPrefix}${start || 1}`;
    }
    if (SETTING_INVOICE.INV_NO_FORMAT === 'TextAndNumber') {
        return match ? increment() : `${SETTING_INVOICE.INV_PREFIX || ''}${SETTING_INVOICE.INV_NO_SEPERATE || ''}${start + 1}`;
    }
    return match ? increment() : String(start || 1);
}

// autoGenerate=false skips the request on mount; use getNextInvNo() to ask for a fresh number
// (e.g. when a draft is confirmed), optionally for another company/branch than the current one.
// resource/settingsKey: 'invoices'/'invoice' (default) or 'quotes'/'quote' for quote numbers.
const useGenInvoiceNo = ({ autoGenerate = true, resource = 'invoices', settingsKey = 'invoice' } = {}) => {
    const user = useSelector((state) => state.auth.user);

    const {data,postData} = usePost();
    const [newInvNo,setNewInvNo] = useState()
    const [loading,setLoading] = useState(autoGenerate);

    const tenantId = tenant()?._id;
    const companySelected  = useSelector((state)=>state.currentCompany)
    const branchSelected = useSelector((state)=>state.currentBranch)

    const settingsOf = (companyId) => arrayToObjKeyValue(getCompanyById(companyId)?.settings?.[settingsKey]);
    const SETTING_INVOICE = settingsOf(companySelected.value);
    const prefixOf = (settings) => btoa(user?._id).substring(1,4)+settings.INV_NO_SEPERATE;
    const userPrefix = prefixOf(SETTING_INVOICE);

    const requestOf = (settings, companyId, branchId) => {
        let _payload = {
            tenant_id:tenantId,
            company_id:companyId,
            branch_id:branchId,
        }
        if(settings.INV_NO_FORMAT === 'Number'){
            return [`${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/${resource}/get-last-inv-no-by-tenant`,_payload];
        }
        _payload.prefix = settings.INV_NO_FORMAT === 'UserAndNumber' ? prefixOf(settings) : settings?.INV_PREFIX
        return [`${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/${resource}/get-last-inv-no-by-tenant-with-prefix`, _payload];
    }

    const genInvoiceNo = () => {
        if(!SETTING_INVOICE) return
        postData(...requestOf(SETTING_INVOICE, companySelected.value, branchSelected.value));
    }

    const getNextInvNo = useCallback(async (companyId = companySelected.value, branchId = branchSelected.value) => {
        const settings = settingsOf(companyId);
        const [url, payload] = requestOf(settings, companyId, branchId);
        const token = localStorage.getItem('token');
        const res = await axios.post(url, payload, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        return nextInvNo(res.data, settings, prefixOf(settings));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companySelected.value, branchSelected.value, tenantId, user?._id]);

    useEffect(()=>{
        if(autoGenerate) genInvoiceNo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[])

    useEffect(()=>{
        if(data || data === 0){
            setNewInvNo(nextInvNo(data, SETTING_INVOICE, userPrefix))
            setLoading(false)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[data])

    return {newInvNo,loading,getNextInvNo}
}

export default useGenInvoiceNo;
